import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Typography } from "@material-ui/core";
import { useTranslation } from "../../hooks/useTranslation";
import TranscriptionParagraph from "./TranscriptionParagraph";
import TranscriptionSearchBar from "./TranscriptionSearchBar";
import { sortSentences } from "../../utils/speakerHelpers";

/**
 * Lista rolável de parágrafos da transcrição (área 4 do painel).
 *
 * Responsabilidades:
 *   - Renderizar todas as `sentences` em ordem temporal.
 *   - Aplicar busca textual com destaque (sem filtrar) e navegação por
 *     contadores prev/next, centralizando o match atual.
 *   - Sincronizar com o player: a frase indicada por `activeSentenceIndex`
 *     recebe destaque e, ao mudar, é centralizada na área de visualização.
 *
 * IMPORTANTE (performance): durante a reprodução, o player notifica o painel
 * a cada 100ms. Para evitar re-render em cascata em toda a lista (que é
 * cara, especialmente com muitas frases), esta lista NÃO recebe `currentTime`
 * — recebe apenas o `activeSentenceIndex` já calculado pelo painel, que
 * muda no ritmo das transições entre frases (≈1 vez por segundo). Combinado
 * com `React.memo` no `TranscriptionParagraph` e callbacks estáveis aqui
 * (refs + `useCallback([])`), apenas os 2 parágrafos afetados (o que perde
 * `active` e o novo) re-renderizam quando o índice muda.
 *
 * Props:
 *   - `transcription`         {speakers, sentences}
 *   - `editable`              boolean
 *   - `activeSentenceIndex`   índice da frase ativa (ou -1)
 *   - `onChange(next)`        notifica alteração no conteúdo
 *   - `onSeek(seconds)`       pedido para o player se reposicionar
 */
function TranscriptionList({
  transcription,
  editable,
  activeSentenceIndex = -1,
  onChange,
  onSeek,
}) {
  const { t } = useTranslation("common");
  /* Memoizado para manter referências estáveis enquanto `transcription` não
     muda (evita loops nos hooks que dependem de `sentences`). */
  const sentences = useMemo(
    () => transcription?.sentences || [],
    [transcription],
  );
  const speakers = useMemo(
    () => transcription?.speakers || [],
    [transcription],
  );

  /* Refs para os dados mutáveis usados pelos callbacks "estáveis" abaixo.
     Manter os callbacks com dependências `[]` é o que permite a memoização
     do `TranscriptionParagraph` funcionar sob a cascata de updates do
     player. */
  const transcriptionRef = useRef(transcription);
  const sentencesRef = useRef(sentences);
  const onChangeRef = useRef(onChange);
  const onSeekRef = useRef(onSeek);

  useEffect(() => {
    transcriptionRef.current = transcription;
  }, [transcription]);
  useEffect(() => {
    sentencesRef.current = sentences;
  }, [sentences]);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onSeekRef.current = onSeek;
  }, [onSeek]);

  const containerRef = useRef(null);
  const paragraphRefs = useRef([]);
  /* Mapeia índice da sentença → array de offsets de matches dentro dela. */
  const matchMapRef = useRef([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [activeMatch, setActiveMatch] = useState({
    sentenceIndex: -1,
    offsetIndex: -1,
  });

  const lastActiveRef = useRef(-1);
  /* Quando a frase ativa muda, centraliza-a no scroll. */
  useEffect(() => {
    if (
      activeSentenceIndex < 0 ||
      activeSentenceIndex === lastActiveRef.current
    ) {
      return;
    }
    lastActiveRef.current = activeSentenceIndex;
    const el = paragraphRefs.current[activeSentenceIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeSentenceIndex]);

  /* Calcula o mapa de matches sempre que a busca ou o conteúdo mudam. */
  useEffect(() => {
    const q = (searchQuery || "").trim();
    if (!q) {
      matchMapRef.current = [];
      setActiveMatch({ sentenceIndex: -1, offsetIndex: -1 });
      return;
    }
    const lower = q.toLowerCase();
    let totalMatches = 0;
    const map = sentences.map((s) => {
      const text = (s?.text || "").toLowerCase();
      const offsets = [];
      let from = 0;
      while (from <= text.length) {
        const idx = text.indexOf(lower, from);
        if (idx === -1) break;
        offsets.push(idx);
        from = idx + Math.max(1, lower.length);
      }
      totalMatches += offsets.length;
      return offsets;
    });
    matchMapRef.current = map;

    /* Se já temos um match ativo válido, mantém; senão escolhe o primeiro. */
    if (totalMatches === 0) {
      setActiveMatch({ sentenceIndex: -1, offsetIndex: -1 });
      return;
    }
    setActiveMatch((prev) => {
      const sIdx = prev.sentenceIndex;
      if (
        sIdx >= 0 &&
        sIdx < map.length &&
        prev.offsetIndex >= 0 &&
        prev.offsetIndex < map[sIdx].length
      ) {
        return prev;
      }
      const firstSentence = map.findIndex((arr) => arr.length > 0);
      return { sentenceIndex: firstSentence, offsetIndex: 0 };
    });
  }, [searchQuery, sentences]);

  /* Quando o match ativo muda, traz para o centro. */
  useEffect(() => {
    if (activeMatch.sentenceIndex < 0) return;
    const el = paragraphRefs.current[activeMatch.sentenceIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeMatch]);

  const totalMatches = matchMapRef.current.reduce(
    (acc, arr) => acc + arr.length,
    0,
  );
  /* Calcula a posição linear (1-based) do match atual entre todos os matches. */
  const currentMatchPosition = useMemo(() => {
    if (activeMatch.sentenceIndex < 0) return 0;
    let pos = 0;
    for (let i = 0; i < activeMatch.sentenceIndex; i += 1) {
      pos += (matchMapRef.current[i] || []).length;
    }
    return pos + activeMatch.offsetIndex + 1;
  }, [activeMatch]);

  const stepMatch = useCallback(
    (direction) => {
      if (totalMatches === 0) return;
      const map = matchMapRef.current;
      const flat = [];
      map.forEach((arr, sIdx) => {
        arr.forEach((_, oIdx) => flat.push({ sentenceIndex: sIdx, offsetIndex: oIdx }));
      });
      if (flat.length === 0) return;
      let currentIdx = flat.findIndex(
        (m) =>
          m.sentenceIndex === activeMatch.sentenceIndex &&
          m.offsetIndex === activeMatch.offsetIndex,
      );
      if (currentIdx === -1) currentIdx = 0;
      const nextIdx =
        (currentIdx + direction + flat.length) % flat.length;
      setActiveMatch(flat[nextIdx]);
    },
    [activeMatch, totalMatches],
  );

  /* Callbacks estáveis (deps []) — leem de refs. Isso permite que o
     `React.memo` em `TranscriptionParagraph` evite re-renders quando o
     `activeSentenceIndex` muda. */
  const handleUpdateSentence = useCallback((index, updates) => {
    const list = sentencesRef.current;
    const current = transcriptionRef.current;
    if (index < 0 || index >= list.length) return;
    const next = list.map((s, i) => (i === index ? { ...s, ...updates } : s));
    const reordered = sortSentences({ ...current, sentences: next });
    onChangeRef.current?.(reordered);
  }, []);

  const handleChangeSpeaker = useCallback((index, speakerId) => {
    handleUpdateSentence(index, { speaker_id: speakerId });
  }, [handleUpdateSentence]);

  const handleSplit = useCallback((index, { updates, newSentence }) => {
    const list = sentencesRef.current;
    const current = transcriptionRef.current;
    if (index < 0 || index >= list.length) return;
    const next = list.map((s, i) => (i === index ? { ...s, ...updates } : s));
    next.splice(index + 1, 0, newSentence);
    onChangeRef.current?.(sortSentences({ ...current, sentences: next }));
  }, []);

  const handleDelete = useCallback((index) => {
    const list = sentencesRef.current;
    const current = transcriptionRef.current;
    if (index < 0 || index >= list.length) return;
    const next = list.filter((_, i) => i !== index);
    onChangeRef.current?.({ ...current, sentences: next });
  }, []);

  /* Repassa os argumentos completos (`seconds` e o opcional `pauseAt`)
     para o painel — o painel usa o `pauseAt` para programar uma pausa
     automática ao fim da frase clicada. */
  const handleSeek = useCallback((seconds, pauseAt) => {
    onSeekRef.current?.(seconds, pauseAt);
  }, []);

  /* Setter estável para popular `paragraphRefs.current[index]` sem criar
     uma função inline diferente a cada render (que invalidaria o memo). */
  const paragraphRefSettersRef = useRef([]);
  const getRefSetter = useCallback((index) => {
    const cache = paragraphRefSettersRef.current;
    if (!cache[index]) {
      cache[index] = (el) => {
        paragraphRefs.current[index] = el;
      };
    }
    return cache[index];
  }, []);

  return (
    <Box className="transcription-list">
      <Box className="transcription-list__header">
        <Typography variant="subtitle2" className="transcription-list__title">
          {t("transcription.list.title", { count: sentences.length })}
        </Typography>
        <TranscriptionSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          currentIndex={currentMatchPosition}
          total={totalMatches}
          onPrev={() => stepMatch(-1)}
          onNext={() => stepMatch(1)}
        />
      </Box>

      <Box ref={containerRef} className="transcription-list__scroll">
        {sentences.length === 0 ? (
          <Typography variant="body2" className="transcription-list__empty">
            {t("transcription.list.empty")}
          </Typography>
        ) : (
          sentences.map((s, index) => {
            /* `activeMatchOccurrence` é o índice da ocorrência dentro
               desta frase (0, 1, 2, ...) que está atualmente focada pela
               navegação prev/next. Se a frase não contém o match ativo,
               passamos -1. O parágrafo então conta as ocorrências enquanto
               renderiza os tokens para destacar a correta. */
            const activeMatchOccurrence =
              activeMatch.sentenceIndex === index
                ? activeMatch.offsetIndex
                : -1;
            return (
              <TranscriptionParagraph
                /* Chave baseada apenas no índice. Incluir `start` aqui
                   provocava remontagem do parágrafo a cada edição de tempo,
                   o que apagava o estado local do `TimestampInput` (texto
                   digitado, mensagem de erro). Como a lista é controlada e
                   `React.memo` já evita re-renders desnecessários, o índice
                   sozinho é suficiente. */
                key={`s-${index}`}
                ref={getRefSetter(index)}
                sentence={s}
                index={index}
                speakers={speakers}
                prevSentence={sentences[index - 1] || null}
                nextSentence={sentences[index + 1] || null}
                editable={editable}
                active={index === activeSentenceIndex}
                searchQuery={searchQuery}
                searchActiveMatchOccurrence={activeMatchOccurrence}
                onUpdate={handleUpdateSentence}
                onChangeSpeaker={handleChangeSpeaker}
                onSplit={handleSplit}
                onDelete={handleDelete}
                onSeek={handleSeek}
              />
            );
          })
        )}
      </Box>
    </Box>
  );
}

export default TranscriptionList;
